import dayjs from 'dayjs';
import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

import { Box, Button, Divider } from '@breathelife/mui';
import ApiService from '../../../../../Services/ApiService';

import { Table } from '../../../../../Components/Table/Table';
import Typography from '../../../../../Components/Typography';
import { AST_DATE_FORMAT } from '../../ApplicationOverview/ApplicationOverviewContainer';
import { ApplicationSupportCeremonyEvents, OneSpanEventStatus as CeremonyEventStatus } from '@breathelife/types';

import { useDispatch } from '../../../../../Hooks';
import { notificationSlice } from '../../../../../ReduxStore/Notification/NotificationSlice';

export function CeremonyEvents(props: { events: Partial<ApplicationSupportCeremonyEvents>[] }): ReactElement {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { events } = props;

  const onReplayClicked = useCallback(
    async (event: Partial<ApplicationSupportCeremonyEvents>): Promise<void> => {
      try {
        await ApiService.replayCeremonyEvent({
          id: event.id,
          name: event.name,
          packageId: event.packageId,
          submissionRunId: (event as any).submissionRunId,
          createdDate: dayjs(event.createdAt).format(AST_DATE_FORMAT),
          sessionUser: event.sessionUser,
        });

        dispatch(
          notificationSlice.actions.setSuccess({
            message: t('notifications.eventReplayed'),
            autoHideDuration: 3000,
          }),
        );
      } catch (error) {
        dispatch(
          notificationSlice.actions.setError({
            message: t('notifications.eventReplayFailed'),
            autoHideDuration: 3000,
          }),
        );
      }
    },
    [dispatch, t],
  );

  const columns = useMemo<Column<Partial<ApplicationSupportCeremonyEvents>>[]>(
    () => [
      {
        Header: t('applicationSupport.eSignature.ceremonyEvent.name'),
        accessor: 'name',
        Cell: function ({ value }): ReactElement {
          return <Typography variant='body5'>{value || '-'}</Typography>;
        },
      },
      {
        Header: t('applicationSupport.eSignature.ceremonyEvent.status'),
        accessor: 'status',
        Cell: function ({ value }): ReactElement {
          return (
            <Typography
              variant='body5'
              color={
                value === CeremonyEventStatus.ERROR
                  ? 'error'
                  : value === CeremonyEventStatus.PROCESSING
                  ? 'warning'
                  : 'success'
              }
            >
              {value || '-'}
            </Typography>
          );
        },
      },
      {
        Header: t('applicationSupport.eSignature.ceremonyEvent.date'),
        accessor: 'createdAt',
        Cell: function ({ value }): ReactElement {
          return <Typography variant='body5'>{value ? dayjs(value).format(AST_DATE_FORMAT) : '-'}</Typography>;
        },
      },
      {
        Header: t('applicationSupport.eSignature.ceremonyEvent.logs'),
        accessor: 'logs',
        Cell: function ({ value }): ReactElement {
          if (!value || !value.length) return <Typography variant='body5'>-</Typography>;

          return (
            <Box>
              {value.map((log, index) => (
                <Typography key={index} variant='body5' color={log.error ? 'error' : 'inherit'}>
                  {log ? JSON.stringify(log) : '-'}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        Header: '',
        accessor: 'id',
        Cell: ({ cell: { value }, row: { original } }): ReactElement => (
          <Box>
            {original.status === CeremonyEventStatus.ERROR && (
              <Button
                variant='contained'
                color='primary'
                size='small'
                fullWidth
                title={`Replay with ${value} ${original.status}`}
                onClick={() => onReplayClicked(original)}
              >
                {t('applicationSupport.eSignature.ceremonyEvent.replay')}
              </Button>
            )}
          </Box>
        ),
      },
    ],
    [onReplayClicked, t],
  );

  return (
    <Box mb={3}>
      <Box pb='1rem'>
        <Typography variant='h2'>{t('applicationSupport.eSignature.ceremonyEvent.header')}</Typography>
      </Box>
      <Divider />
      {(!events || !events.length) && (
        <Box mt='1rem'>
          <Typography variant='body5'>{t('applicationSupport.eSignature.ceremonyEvent.noEventsFound')}</Typography>
        </Box>
      )}

      {!!events?.length && (
        <Box mt={2}>
          <Table<Partial<ApplicationSupportCeremonyEvents>> columns={columns} data={events} autoHeight={true} />
        </Box>
      )}
    </Box>
  );
}
